import { Component, OnInit, Output, EventEmitter, Input } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Store, select } from "@ngrx/store";
import { NGXLogger } from "ngx-logger";
import { filter } from "rxjs/operators";
import {
  ForgotPasswordSubmitting,
  SignInActionTypes,
  SignedInAction,
  SignedOutAction,
  SigningInAction
} from "../../../../app/components/allusers/signin/signin.actions";
import { SignInState } from "../../../../app/components/allusers/signin/signin.model";
import {
  getSignInCurrentState,
  getSignedInUser,
  isForgotPasswordFailed,
  isForgotPasswordSubmitted,
  isSignInFailure,
  isSignedIn,
  isSigningIn,
} from "../../../../app/components/allusers/signin/signin.selector";
import { AuthService } from "../../../../app/services/auth-service/auth.service";
import { RouterService } from "../../../../app/services/route-service/router.service";
import { SignUpState } from "../signup/signup.model";
import { GoogleAuthService } from "src/app/services/google-auth.service";
import { environment } from "src/environments/environment";

declare global {
  interface Window {
    handleCredentialResponse: (response: any) => void;
  }
}

declare const google: any;

@Component({
  selector: "app-signin",
  templateUrl: "./signin.component.html",
  styleUrls: ["./signin.component.css"],
})
export class SigninComponent implements OnInit {

  isSigningIn$ = this.signInStore.pipe(select(isSigningIn));
  isSignedIn$ = this.signInStore.pipe(select(isSignedIn));
  isForgotPasswordSubmitted$ = this.signInStore.pipe(
    select(isForgotPasswordSubmitted),
    filter((val) => val)
  );
  showForgotPasswordSuccess = false;
  isForgotPasswordFailed$ = this.signInStore.pipe(
    select(isForgotPasswordFailed),
    filter((val) => !!val.error)
  );


  currentState$ = this.signInStore.pipe(select(getSignInCurrentState));
  currentState = "";
  currentUser$ = this.signInStore.pipe(select(getSignedInUser));
  currentUser;

  successMessage: any = null;
  errorMessage: any = null;
  isSignInFailure$ = this.signInStore.pipe(
    select(isSignInFailure),
    filter((val) => (val ? true : false))
  );

  showPassword = false;

  // Accepts either an email or a username. Cognito has email configured as an
  // alias, so it resolves either form to the right user at sign-in.
  userForm = new FormGroup({
    email: new FormControl("", [Validators.required]),
    password: new FormControl(""),
  });

  @Input() sessionParam: string | null = null;
  @Output() triggerSignup = new EventEmitter<void>();

  constructor(
    private authService: AuthService,
    private signInStore: Store<SignInState>,
    private signUpStore: Store<SignUpState>,
    private routeService: RouterService,
    private logger: NGXLogger,
    private googleAuthService: GoogleAuthService
  ) { }

  ngOnInit() {
    this.isSignInFailure$.subscribe((val: any) =>
      val.error ? (this.errorMessage = val.error) : (this.errorMessage = null)
    );

    this.currentState$.subscribe((state) => {
      this.currentState = state ? state : "";

      // Call initGoogleSignIn when the signin form div should be shown
      if (state === 'SignedOut' || state === 'SignInFailed') {
        // Use setTimeout to ensure the DOM element is available after *ngIf renders
        setTimeout(() => {
          this.initGoogleSignIn();
        }, 0);
      }
    });

    this.currentUser$.subscribe((user) => {
      this.currentUser = user;
    });

    this.isSignedIn$.subscribe((user) => {
      if (user) {
        this.routeService.gotoDashboard();
      }
    });

    this.isForgotPasswordSubmitted$.subscribe(() => {
      this.showForgotPasswordSuccess = true;
      this.signInStore.dispatch(
        new SignedOutAction({
          currentState: SignInActionTypes.SignedOut,
        } as SignInState)
      );
    });

    this.isForgotPasswordFailed$.subscribe((state) => {
      this.errorMessage = state.error;
    });

  }

  loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject('Failed to load Google script');
      document.head.appendChild(script);
    });
  }

  initGoogleSignIn(): void {
    this.loadGoogleScript().then(() => {
      google.accounts.id.initialize({
        client_id: environment.google.clientId,
        callback: this.handleGoogleSignIn.bind(this),
      });

      google.accounts.id.renderButton(
        document.getElementById('google-signin-button'),
        { theme: 'outline', size: 'large' }
      );
    });
  }

  async handleGoogleSignIn(response: any): Promise<void> {    
    // this.logger.debug('ID Token:', response.credential);
    this.signInStore.dispatch(
      new SigningInAction({
        currentState: SignInActionTypes.SigningIn,
      } as SignInState)
    );
    try {
      await this.authService.federatedSignIn('google', response.credential);
    } catch (error) {
      this.errorMessage = error.message;
      this.signInStore.dispatch(
        new SignedOutAction({
          currentState: SignInActionTypes.SignInFailed,
          error: error.message,
        } as SignInState)
      );
    } 
  }

  initMessages() {
    this.showForgotPasswordSuccess = false;
    this.errorMessage = null;
  }

  async signin() {
    if (this.userForm.valid) {
      this.initMessages();
      // Email is configured as a Cognito alias, so we sign in with the email
      // directly and Cognito resolves it to the underlying user (works for both
      // legacy accounts and accounts whose username is derived from the email).
      const email = (this.userForm.value.email || "").trim().toLowerCase();
      this.signInStore.dispatch(
        new SigningInAction({
          currentState: SignInActionTypes.SigningIn,
          user: { membername: email },
        } as SignInState)
      );

      await this.authService
        .signIn(email, this.userForm.value.password);
    }
  }

  async forgotUserPassword() {
    if (this.userForm.controls.email.valid) {
      const email = (this.userForm.value.email || "").trim().toLowerCase();
      this.signInStore.dispatch(
        new ForgotPasswordSubmitting({
          currentState: SignInActionTypes.ForgotPasswordSubmitting,
          user: { membername: email },
        } as SignInState)
      );

      this.initMessages();
      try {
        await this.authService.forgotPassword(email);
      } catch (error) {
        this.errorMessage = error.message;
      }

    } else {
      this.errorMessage = "A valid email is required.";
    }
  }
}
